import { useState } from 'react';

/**
 * Import/export controls. Export writes the crawler's .dccchar text into the
 * box for the user to copy; Import parses pasted text and calls back with it.
 * Text-based (not file upload) because it works identically on desktop and
 * mobile and inside the OBR iframe sandbox, matching how the Shadowdark sheet
 * and other extensions move characters around.
 */
export interface PortabilityHandlers {
  /** Produce export text for the current crawler, or null if none/failed. */
  onExport?: () => Promise<{ text: string; fileName: string } | { error: string }>;
  /** Import pasted text as a new crawler; returns an error string or void. */
  onImport: (text: string) => Promise<string | void>;
}

export function PortabilityPanel({ handlers, canExport }: { handlers: PortabilityHandlers; canExport: boolean }) {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [note, setNote] = useState('');

  async function doExport() {
    if (!handlers.onExport) return;
    const r = await handlers.onExport();
    if ('error' in r) { setNote(r.error); return; }
    setText(r.text);
    setNote(`Exported ${r.fileName}. Copy the text below to save or share it.`);
  }

  async function doImport() {
    if (!text.trim()) { setNote('Paste exported crawler text into the box first.'); return; }
    const err = await handlers.onImport(text.trim());
    setNote(typeof err === 'string' ? err : 'Imported crawler successfully.');
    if (typeof err !== 'string') setText('');
  }

  return (
    <section className="dcc-portability" aria-label="import export">
      <button className="dcc-portability-toggle" onClick={() => setOpen(!open)}>
        {open ? '▾' : '▸'} Import / Export
      </button>
      {open ? (
        <div className="dcc-portability-body">
          <textarea
            className="dcc-portability-text"
            value={text}
            onChange={(e: { target: { value: string } }) => setText(e.target.value)}
            placeholder="Paste crawler text here to import, or click Export to fill this box."
            rows={5}
            aria-label="crawler text"
          />
          <div className="dcc-portability-buttons">
            {canExport ? <button onClick={doExport}>Export current</button> : null}
            <button onClick={doImport}>Import from text</button>
          </div>
          {note ? <p className="dcc-action-note" role="status">{note}</p> : null}
        </div>
      ) : null}
    </section>
  );
}
